import { ApiTransport } from './ApiTransport';
import type {
  AzureBranch,
  AzureProject,
  AzureRepository,
  ManagedProject,
  ProviderId,
  Repository,
  RepositoryBranch,
  RepositoryOwner,
  RepositoryProject,
  ScmProviderDescriptor,
} from './types';

export class ScmClient {
  constructor(private readonly transport: ApiTransport) {}

  async listProjects(): Promise<AzureProject[]> {
    const response = await this.transport.request(`/azure/projects`);

    if (!response.ok) {
      throw new Error('Failed to list Azure DevOps projects');
    }

    return response.json();
  }

  async listRepositories(projectId: string): Promise<AzureRepository[]> {
    const response = await this.transport.request(
      `/azure/projects/${encodeURIComponent(projectId)}/repositories`,
    );

    if (!response.ok) {
      throw new Error('Failed to list repositories');
    }

    return response.json();
  }

  async listBranches(
    projectId: string,
    repoId: string,
  ): Promise<AzureBranch[]> {
    const response = await this.transport.request(
      `/azure/projects/${encodeURIComponent(
        projectId,
      )}/repositories/${encodeURIComponent(repoId)}/branches`,
    );

    if (!response.ok) {
      throw new Error('Failed to list branches');
    }

    return response.json();
  }

  async listScmProviders(): Promise<ScmProviderDescriptor[]> {
    const response = await this.transport.request(`/scm/providers`);
    if (!response.ok) throw new Error('Failed to list SCM providers');
    return response.json();
  }

  async listScmOwners(provider: ProviderId): Promise<RepositoryOwner[]> {
    const response = await this.transport.request(
      `/scm/${encodeURIComponent(provider)}/owners`,
    );
    if (!response.ok) throw new Error('Failed to list SCM owners');
    return response.json();
  }

  async listManagedProjects(): Promise<ManagedProject[]> {
    const providers = await this.listScmProviders();
    const ownersPerProvider = await Promise.all(
      providers.map(async p => {
        try {
          const owners = await this.listScmOwners(p.id);
          return owners.map<ManagedProject>(o => ({
            name: o.name,
            providerId: p.id,
            providerLabel: p.displayName,
          }));
        } catch {
          // Provider isolado não bloqueia a página: por exemplo, um provider
          // configurado mas com credenciais expiradas continua aparecendo no
          // dropdown de outros (Azure ainda funciona se o GitHub falhar).
          return [];
        }
      }),
    );
    return ownersPerProvider.flat();
  }

  async listAllRepositoriesForOwner(
    provider: ProviderId,
    owner: string,
  ): Promise<Repository[]> {
    const providers = await this.listScmProviders();
    const descriptor = providers.find(p => p.id === provider);
    if (!descriptor) {
      throw new Error(`Provider '${provider}' is not registered`);
    }

    if (!descriptor.hierarchy.hasProject) {
      // GitHub e similares: lista direta sob o owner.
      return await this.listScmRepositories(provider, owner);
    }

    // Azure DevOps e outros com camada de project: enumera os projects do
    // owner e agrega repos de todos. Falha em um project não interrompe os
    // demais — devolve só os que carregaram com sucesso.
    const projects = await this.listScmProjects(provider, owner);
    const reposPerProject = await Promise.all(
      projects.map(async project => {
        try {
          return await this.listScmRepositories(provider, owner, project.name);
        } catch {
          return [];
        }
      }),
    );
    return reposPerProject.flat();
  }

  async listScmProjects(
    provider: ProviderId,
    owner: string,
  ): Promise<RepositoryProject[]> {
    const response = await this.transport.request(
      `/scm/${encodeURIComponent(provider)}/owners/${encodeURIComponent(
        owner,
      )}/projects`,
    );
    if (!response.ok) throw new Error('Failed to list SCM projects');
    return response.json();
  }

  async listScmRepositories(
    provider: ProviderId,
    owner: string,
    project?: string,
  ): Promise<Repository[]> {
    const path = project
      ? `/scm/${encodeURIComponent(provider)}/owners/${encodeURIComponent(
          owner,
        )}/projects/${encodeURIComponent(project)}/repositories`
      : `/scm/${encodeURIComponent(provider)}/owners/${encodeURIComponent(
          owner,
        )}/repositories`;
    const response = await this.transport.request(path);
    if (!response.ok) throw new Error('Failed to list SCM repositories');
    return response.json();
  }

  async listScmBranches(
    provider: ProviderId,
    repo: Pick<Repository, 'externalId' | 'owner' | 'project' | 'name'>,
  ): Promise<RepositoryBranch[]> {
    // Identificadores do repositório vão na query string, nunca no path: o
    // `externalId` do GitHub é `owner/repo` (contém `/`), que no path vira
    // `%2F` e é rejeitado com 404 pelo ingress de produção (Envoy). Na query,
    // o `URLSearchParams` codifica com segurança e o proxy não interfere.
    const params = new URLSearchParams({
      externalId: repo.externalId,
      owner: repo.owner,
      name: repo.name,
    });
    if (repo.project) params.set('project', repo.project);
    const response = await this.transport.request(
      `/scm/${encodeURIComponent(
        provider,
      )}/repositories/branches?${params.toString()}`,
    );
    if (!response.ok) throw new Error('Failed to list branches');
    return response.json();
  }
}
